import type { PropsWithChildren } from 'react'
import { useMemo, useReducer } from 'react'
import type {
  DashboardBook,
  DashboardUserProfile,
} from '@services/firebase'
import { normalizeDashboardUserProfile } from '@services/firebase'
import type { LearningLesson } from '@services/firebase/learning'
import {
  DashboardStateContext,
  initialDashboardState,
  type DashboardListeningQuizSession,
  type DashboardSettingsTab,
  type DashboardState,
  type DashboardStateActions,
  type DashboardStateContextValue,
} from '@features/dashboard/store/dashboard-state'

type DashboardAction =
  | { type: 'merge-my-user'; payload: Partial<DashboardUserProfile> }
  | { type: 'set-book'; payload: DashboardBook | null }
  | { type: 'set-books'; payload: DashboardBook[] }
  | { type: 'set-bootstrapping'; payload: boolean }
  | { type: 'set-enter-key-premium'; payload: boolean }
  | { type: 'set-error-message'; payload: string | null }
  | { type: 'set-lesson'; payload: LearningLesson | null }
  | { type: 'set-lessons'; payload: LearningLesson[] }
  | { type: 'set-lessons-book-id'; payload: string | null }
  | { type: 'set-lessons-error-message'; payload: string | null }
  | { type: 'set-lessons-loading'; payload: boolean }
  | {
      type: 'set-listening-quiz-session'
      payload: DashboardListeningQuizSession | null
    }
  | { type: 'set-my-user'; payload: DashboardUserProfile | null }
  | { type: 'set-screen'; payload: number }
  | { type: 'set-settings-tab'; payload: DashboardSettingsTab | null }
  | { type: 'set-select-premium-account-payment'; payload: boolean }
  | { type: 'set-show-change-daily-goal'; payload: boolean }
  | { type: 'set-show-language'; payload: boolean }

function dashboardReducer(
  state: DashboardState,
  action: DashboardAction,
): DashboardState {
  switch (action.type) {
    case 'merge-my-user':
      return {
        ...state,
        myUser: state.myUser
          ? normalizeDashboardUserProfile({
              ...state.myUser,
              ...action.payload,
            })
          : null,
      }
    case 'set-book':
      return { ...state, book: action.payload }
    case 'set-books':
      return { ...state, books: action.payload }
    case 'set-bootstrapping':
      return { ...state, isBootstrapping: action.payload }
    case 'set-enter-key-premium':
      return { ...state, enterKeyPremium: action.payload }
    case 'set-error-message':
      return { ...state, errorMessage: action.payload }
    case 'set-lesson':
      return { ...state, lesson: action.payload }
    case 'set-lessons':
      return { ...state, lessons: action.payload }
    case 'set-lessons-book-id':
      return { ...state, lessonsBookId: action.payload }
    case 'set-lessons-error-message':
      return { ...state, lessonsErrorMessage: action.payload }
    case 'set-lessons-loading':
      return { ...state, isLessonsLoading: action.payload }
    case 'set-listening-quiz-session':
      return { ...state, listeningQuizSession: action.payload }
    case 'set-my-user':
      return { ...state, myUser: normalizeDashboardUserProfile(action.payload) }
    case 'set-screen':
      return { ...state, screen: action.payload }
    case 'set-settings-tab':
      return { ...state, settingsTab: action.payload }
    case 'set-select-premium-account-payment':
      return { ...state, selectPremiumAccountPayment: action.payload }
    case 'set-show-change-daily-goal':
      return { ...state, showChangeDailyGoal: action.payload }
    case 'set-show-language':
      return { ...state, showLanguage: action.payload }
    default:
      return state
  }
}

export function DashboardStateProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(dashboardReducer, initialDashboardState)

  const actions = useMemo<DashboardStateActions>(
    () => ({
      mergeMyUser: (user) => {
        dispatch({ type: 'merge-my-user', payload: user })
      },
      setBook: (book) => {
        dispatch({ type: 'set-book', payload: book })
      },
      setBooks: (books) => {
        dispatch({ type: 'set-books', payload: books })
      },
      setBootstrapping: (isBootstrapping) => {
        dispatch({ type: 'set-bootstrapping', payload: isBootstrapping })
      },
      setEnterKeyPremium: (enterKeyPremium) => {
        dispatch({ type: 'set-enter-key-premium', payload: enterKeyPremium })
      },
      setErrorMessage: (errorMessage) => {
        dispatch({ type: 'set-error-message', payload: errorMessage })
      },
      setLesson: (lesson) => {
        dispatch({ type: 'set-lesson', payload: lesson })
      },
      setLessons: (lessons) => {
        dispatch({ type: 'set-lessons', payload: lessons })
      },
      setLessonsBookId: (lessonsBookId) => {
        dispatch({ type: 'set-lessons-book-id', payload: lessonsBookId })
      },
      setLessonsErrorMessage: (lessonsErrorMessage) => {
        dispatch({
          type: 'set-lessons-error-message',
          payload: lessonsErrorMessage,
        })
      },
      setLessonsLoading: (isLessonsLoading) => {
        dispatch({ type: 'set-lessons-loading', payload: isLessonsLoading })
      },
      setListeningQuizSession: (listeningQuizSession) => {
        dispatch({
          type: 'set-listening-quiz-session',
          payload: listeningQuizSession,
        })
      },
      setMyUser: (user) => {
        dispatch({ type: 'set-my-user', payload: user })
      },
      setScreen: (screen) => {
        dispatch({ type: 'set-screen', payload: screen })
      },
      setSettingsTab: (settingsTab) => {
        dispatch({ type: 'set-settings-tab', payload: settingsTab })
      },
      setSelectPremiumAccountPayment: (selectPremiumAccountPayment) => {
        dispatch({
          type: 'set-select-premium-account-payment',
          payload: selectPremiumAccountPayment,
        })
      },
      setShowChangeDailyGoal: (showChangeDailyGoal) => {
        dispatch({
          type: 'set-show-change-daily-goal',
          payload: showChangeDailyGoal,
        })
      },
      setShowLanguage: (showLanguage) => {
        dispatch({ type: 'set-show-language', payload: showLanguage })
      },
    }),
    [],
  )

  const value = useMemo<DashboardStateContextValue>(
    () => ({
      actions,
      state,
    }),
    [actions, state],
  )

  return (
    <DashboardStateContext.Provider value={value}>
      {children}
    </DashboardStateContext.Provider>
  )
}
